import {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {serializeFilters} from 'nutshell-core';

import type {FilterObject} from 'nutshell-core/types';

import type {NutshellState} from '../../store';

import {EntityListControls} from '../entity-list/header/entity-list-controls';
// @ts-expect-error
import {getLeadsSchema} from '../master-dashboard/dashboard-selectors';
import {getListMeta, getListIsLoading} from '../reports/engagement/leads/leads-selectors';
import type {ReportURLParams} from '../reports/report-types';

interface Props {
    activeFilters: FilterObject[];
    onParamChange: (params: any, replace: boolean) => void;
    urlParams: ReportURLParams<'leads'>;
}

export function EngagementBarLeadsFilterControls(props: Props) {
    const {activeFilters, onParamChange, urlParams} = props;
    const schema = useSelector((state: NutshellState) => getLeadsSchema(state));
    const listMeta = useSelector(getListMeta);
    const isLoading = useSelector(getListIsLoading);

    const count = listMeta?.count ?? 0;
    const currentSearch = typeof urlParams?.q === 'string' ? urlParams.q : null;
    const listParams = useMemo(() => ({...urlParams?.list, page: 1}), [urlParams?.list]);

    const handleAddFilters = useCallback(
        (filter: FilterObject) => {
            const newFilters = [...activeFilters, filter];
            const serialized = serializeFilters(newFilters);

            onParamChange({filter: serialized, list: listParams}, false);
        },
        [activeFilters, onParamChange, listParams]
    );

    const handleRemoveFilters = useCallback(
        (filtersToRemove: FilterObject[] | FilterObject) => {
            const filtersArray = Array.isArray(filtersToRemove)
                ? filtersToRemove
                : [filtersToRemove];
            const filterKeysToRemove = new Set(filtersArray.map((f) => Object.keys(f)[0]));
            const newFilters = activeFilters.filter(
                (f) => !filterKeysToRemove.has(Object.keys(f)[0])
            );
            const serialized = serializeFilters(newFilters);

            onParamChange({filter: serialized, list: listParams}, false);
        },
        [activeFilters, onParamChange, listParams]
    );

    const handleRemoveAllFilters = useCallback(() => {
        onParamChange({filter: {}, q: undefined, list: listParams}, false);
    }, [onParamChange, listParams]);

    const handleUpdateFilter = useCallback(
        (oldFilter: FilterObject, newFilter: FilterObject) => {
            const oldFilterKey = Object.keys(oldFilter)[0];
            const newFilters = activeFilters.map((f) => {
                const filterKey = Object.keys(f)[0];

                return filterKey === oldFilterKey ? newFilter : f;
            });
            const serialized = serializeFilters(newFilters);

            onParamChange({filter: serialized, list: listParams}, false);
        },
        [activeFilters, onParamChange, listParams]
    );

    const handleUpdateSearch = useCallback(
        (searchValue: string) => {
            onParamChange({q: searchValue || undefined, list: listParams}, false);
        },
        [onParamChange, listParams]
    );

    if (!schema || Object.keys(schema).length === 0) {
        return null;
    }

    return (
        <EntityListControls
            currentView='list'
            entityType='leads'
            onViewChange={() => {}}
            schema={schema || {}}
            activeFilters={activeFilters}
            visibleActiveFilters={activeFilters}
            isRefreshingFilters={isLoading}
            entityCount={count}
            addFilters={handleAddFilters}
            removeFilters={handleRemoveFilters}
            removeAllFilters={handleRemoveAllFilters}
            updateFilter={handleUpdateFilter}
            currentSearch={currentSearch}
            updateSearch={handleUpdateSearch}
            isViewingAudience={false}
            hideViewSelector={true}
            hideFilterButton={true}
        />
    );
}
